import React, { type ReactElement, useState } from 'react'
import ThemeInputDialog from 'common/ui/theme-input-dialog/ThemeInputDialog'
import { FormControlLabel, FormGroup, Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { editVendorSpecialties } from 'features/vendor/vendor.service'
import { type LookupItem, LookupTypes } from 'features/lookup/lookup.types'
import ThemeCheckbox from 'common/ui/theme-checkbox'

import styles from './SpecialitiesDialog.module.scss'

interface SpecialitiesDialogProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const SpecialitiesDialog: React.FC<SpecialitiesDialogProps> = ({ isOpen, setIsOpen }): ReactElement => {
  const dispatch = useAppDispatch()
  const { vendor, vendorSpecialtiesStatus } = useAppSelector((state) => state.vendor)
  const { lookup } = useAppSelector((state) => state.lookup)
  const [selectedSpecialities, setSelectedSpecialities] = useState<LookupItem[]>(vendor?.specialties ?? [])
  const initialState: LookupItem[] = vendor?.specialties ?? []

  const areEqual = (): boolean => {
    const idsInitialState = initialState
      .map((item) => item.id)
      .sort((a, b) => a - b)
      .toString()
    const idsSelectedSpecialities = selectedSpecialities
      .map((item) => item.id)
      .sort((a, b) => a - b)
      .toString()

    return idsInitialState === idsSelectedSpecialities
  }

  const handleVendorUpdate = async (): Promise<void> => {
    await dispatch(editVendorSpecialties({ specialities: selectedSpecialities }))
    setIsOpen(false)
  }

  const handleCheckboxChange = (specialty: LookupItem): void => {
    if (selectedSpecialities.some((item) => item.id === specialty.id)) {
      setSelectedSpecialities(selectedSpecialities.filter((item) => item.id !== specialty.id))
    } else {
      setSelectedSpecialities([...selectedSpecialities, specialty])
    }
  }

  return (
    <ThemeInputDialog
      open={isOpen}
      title="Specijalnosti"
      description="Imate mogućnost da izaberete maksimum 6 specijalnosti za koje imate najviše iskustva. Ove informacije su informativne i ne morate ih popuniti da bi učestvovali na tenderima."
      status={vendorSpecialtiesStatus}
      onClose={() => {
        setIsOpen(false)
      }}
      confirmButtonText="snimi"
      declineButtonText="izađi"
      confirmButtonDisabled={areEqual()}
      confirmButtonAction={() => {
        void handleVendorUpdate()
      }}
      PaperProps={{
        sx: {
          width: '850px',
          height: '650px',
        },
      }}
    >
      <form className={styles.container}>
        <div className={styles.scrollableWrapper}>
          <div className={styles.scrollable}>
            <FormGroup>
              {lookup[LookupTypes.speciality]?.map((specialty) => (
                <FormControlLabel
                  className={styles.increasedGap}
                  key={specialty.id}
                  control={
                    <ThemeCheckbox
                      className={styles.item}
                      checked={selectedSpecialities.some((item) => item.id === specialty.id)}
                      onChange={() => {
                        handleCheckboxChange(specialty)
                      }}
                      disabled={
                        selectedSpecialities?.length >= 6 &&
                        !selectedSpecialities.some((item) => item.id === specialty.id)
                      }
                    />
                  }
                  label={
                    <Typography sx={{ marginLeft: '10px' }} variant="title3">
                      {specialty.name}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </div>
        </div>
      </form>
    </ThemeInputDialog>
  )
}

export default SpecialitiesDialog
